import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { PiWarningCircleLight } from "react-icons/pi";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import ClientAuthContext from "../../context/ClientAuthContext";
import { serverInstance } from "../../service/axios";
import { formatAmount, formatPricingDate, getPricingForTier, getTierLabel, hasMemberPricingAccess } from "../meetings/meetingPricing";
import "../meetings/meetingRegister.scss";
import EventPayment from "./EventPayment";

const getAuthHeaders = (token) => ({
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
  "Content-Type": "application/json",
});

const EventRegister = ({ data, language }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get("event_id")?.trim() || params.id || "";
  const { userData } = useContext(ClientAuthContext);
  const [registered, setRegistered] = useState(false);
  const [paid, setPaid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncingDetails, setSyncingDetails] = useState(false);
  const [eventLoading, setEventLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [warningMsg, setWarningMsg] = useState("");
  const [eventInfo, setEventInfo] = useState(null);
  const [capacityFull, setCapacityFull] = useState(false);
  const [savedResident, setSavedResident] = useState(null);
  const [formData, setFormData] = useState({
    last_name: "",
    first_name: "",
    mobile_number: "",
    email: "",
    resident: false,
  });

  const memberPricing = useMemo(() => getPricingForTier({ meetingInfo: eventInfo, tier: "member", allowDiscountOnly: true }), [eventInfo]);
  const standardPricing = useMemo(() => getPricingForTier({ meetingInfo: eventInfo, tier: "standard", allowDiscountOnly: true }), [eventInfo]);
  const residentPricing = useMemo(() => getPricingForTier({ meetingInfo: eventInfo, tier: "resident", allowDiscountOnly: true }), [eventInfo]);
  const pricingRows = [standardPricing, memberPricing, residentPricing].filter(Boolean);
  const membershipActive = hasMemberPricingAccess(userData);
  const visiblePricingRows = membershipActive
    ? [memberPricing, residentPricing].filter(Boolean)
    : pricingRows;
  const paidUntilValue = typeof userData?.paid_until === "string"
    ? userData.paid_until.trim()
    : userData?.paid_until;
  const clientMembershipExpiredOrEmpty = !!userData?.id && (!paidUntilValue || !membershipActive);
  const hasPaidFee = pricingRows.some((row) => Number(row.amount) > 0);
  const canChooseResident = !!residentPricing;
  const hasCapacityLimit = eventInfo?.limit_capacity === true
    && eventInfo?.capacity !== undefined
    && eventInfo?.capacity !== null
    && String(eventInfo.capacity).trim() !== "";
  const capacityLimit = hasCapacityLimit && !Number.isNaN(Number(eventInfo.capacity))
    ? Number(eventInfo.capacity).toLocaleString(language ? "en-US" : "mn-MN")
    : eventInfo?.capacity;
  const blockForCapacity = capacityFull && !paid;
  const blockNewRegistration = capacityFull && !registered;
  const showCapacityLimitCard = hasCapacityLimit && (!registered || (hasPaidFee && !paid));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrorMsg("");
    setSuccessMsg("");
    setWarningMsg("");
  };

  const handleResidentChange = (e) => {
    setFormData((prev) => ({ ...prev, resident: e.target.checked }));
    setErrorMsg("");
    setSuccessMsg("");
    setWarningMsg("");
  };

  const syncProfileDetailsToForm = (profile) => {
    setFormData((prev) => ({
      ...prev,
      last_name: profile?.last_name || "",
      first_name: profile?.first_name || "",
      mobile_number: profile?.mobile_number || "",
      email: profile?.email || "",
    }));
  };

  const syncDetails = () => {
    const token = localStorage.getItem("msidclienttoken");
    if (!token) {
      return;
    }

    setSyncingDetails(true);
    setErrorMsg("");
    setSuccessMsg("");

    serverInstance.post("/client", {}, {
      headers: getAuthHeaders(token),
    })
      .then((res) => {
        syncProfileDetailsToForm(res?.data?.data || {});
        setSuccessMsg(data.detailsSynced);
      })
      .catch((err) => {
        setErrorMsg(
          err?.response?.data?.message || data.error
        );
      })
      .finally(() => setSyncingDetails(false));
  };

  const getEventAttendee = useCallback(() => {
    if (!eventId) {
      return;
    }

    setLoading(true);
    setRegistered(false);
    setPaid(false);
    setSavedResident(null);
    const token = localStorage.getItem("msidclienttoken");
    serverInstance
      .get(`/client/event-attendee?event_id=${encodeURIComponent(eventId)}`, {
        headers: getAuthHeaders(token),
      })
      .then((res) => {
        const attendee = res?.data?.data;
        if (attendee && attendee.email) {
          const isPaidForEvent = !!attendee.paid_at;
          setRegistered(true);
          setPaid(isPaidForEvent);
          setSavedResident(!!attendee.resident);
          setFormData((prev) => ({
            ...prev,
            last_name: attendee.last_name || prev.last_name,
            first_name: attendee.first_name || prev.first_name,
            mobile_number: attendee.mobile_number || prev.mobile_number,
            email: attendee.email || prev.email,
            resident: !!attendee.resident,
          }));
        }
      })
      .catch((err) => {
        if (err?.response?.status !== 404) {
          setErrorMsg(err?.response?.data?.message || data.error);
        }
      })
      .finally(() => setLoading(false));
  }, [data.error, eventId]);

  const getRegistrationPayload = () => ({
    ...formData,
    email: formData.email.toLowerCase(),
    event_id: eventId,
  });

  const validate = () => {
    const mobilePattern = /^[0-9]{8}$/;
    if (!formData.last_name.trim()) {
      setErrorMsg(data.last_nameRequired);
      return false;
    }
    if (!formData.first_name.trim()) {
      setErrorMsg(data.first_nameRequired);
      return false;
    }
    if (!mobilePattern.test(formData.mobile_number)) {
      setErrorMsg(data.invalidMobile);
      return false;
    }
    if (
      !formData.email.trim() ||
      !/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email)
    ) {
      setErrorMsg(data.invalidEmail);
      return false;
    }
    return true;
  };

  const submit = (e) => {
    e.preventDefault();

    if (!eventId) {
      setErrorMsg(data.eventNotSelected);
      return;
    }

    if (!validate()) return;

    if (blockNewRegistration) {
      setErrorMsg(data.capacityFull);
      return;
    }

    if (blockForCapacity) {
      setErrorMsg(data.capacityFull);
      return;
    }

    setLoading(true);
    const token = localStorage.getItem("msidclienttoken");
    const payload = getRegistrationPayload();
    const wasRegistered = registered;

    serverInstance.post("/client/event-attendee", payload, {
      headers: getAuthHeaders(token),
    })
      .then(() => {
        setRegistered(true);
        setSavedResident(!!payload.resident);
        setErrorMsg("");
        setWarningMsg("");
        setSuccessMsg(wasRegistered
          ? data.registrationSaved
          : (!hasPaidFee ? data.registrationCompleted : ""));
      })
      .catch((err) => {
        if (err?.response?.status === 409) {
          if (err.response?.data?.capacity_full) {
            setErrorMsg(data.capacityFull);
            setSuccessMsg("");
            setCapacityFull(true);
            return;
          }
          const alreadyPaid = !!err.response?.data?.paid;
          const alreadyRegistered = !!err.response?.data?.already_registered;
          setRegistered(true);
          setPaid(alreadyPaid);
          if (alreadyRegistered) {
            setWarningMsg(data.alreadyRegistered);
            setErrorMsg("");
            setSuccessMsg("");
          } else if (alreadyPaid) {
            setErrorMsg("");
            setWarningMsg("");
            setSuccessMsg(data.alreadyRegistered);
          } else {
            setErrorMsg(err?.response?.data?.message || data.error);
            setSuccessMsg("");
            setWarningMsg("");
          }
        } else {
          setErrorMsg(err?.response?.data?.message || data.error);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!eventId) {
      return;
    }
    setEventLoading(true);
    setCapacityFull(false);
    const loadEvent = serverInstance.post("/app/item", { class: "event", id: eventId }, {
      headers: {
        "Content-Type": "application/json",
      }
    });
    const loadCapacity = serverInstance.get(`/app/event-capacity?event_id=${encodeURIComponent(eventId)}`);

    Promise.allSettled([loadEvent, loadCapacity])
      .then(([eventResult, capacityResult]) => {
        if (eventResult.status === "fulfilled") {
          setEventInfo(eventResult.value?.data?.item || null);
        } else {
          setEventInfo(null);
        }

        if (capacityResult.status === "fulfilled") {
          setCapacityFull(capacityResult.value?.data?.capacity_full === true);
        } else {
          setCapacityFull(false);
        }
      })
      .finally(() => setEventLoading(false));
  }, [eventId]);

  useEffect(() => {
    if (userData.id) {
      setFormData((prev) => ({
        ...prev,
        last_name: userData.last_name || "",
        first_name: userData.first_name || "",
        mobile_number: userData.mobile_number || "",
        email: userData.email || "",
      }));

      getEventAttendee();
    }
  }, [userData, getEventAttendee]);

  const submitButtonText = loading
    ? data.loading
    : registered
      ? paid
        ? data.registered
        : data.save
      : data.registerForEvent;

  return (
    <div className="sign-container">
      <div className="sign-container-card">
        <div>
          <h2>{data.title}</h2>
          {eventLoading ? (
            <div className="form-div">{data.loadingEvent}</div>
          ) : null}
          {eventInfo ? (
            <div className="form-div">
              <strong>{data.eventLabel}:</strong> {eventInfo.title}
              <br />
              <strong>{data.dateLabel}:</strong>{" "}
              {eventInfo.dt ? new Date(eventInfo.dt).toLocaleString(language ? "en-US" : "mn-MN") : "-"}
              <br />
              <strong>{data.locationLabel}:</strong> {eventInfo.location || "-"}
            </div>
          ) : null}

          {showCapacityLimitCard ? (
            <div className={`capacity-limit-card${capacityFull ? " is-full" : ""}`}>
              <div className="capacity-limit-label">
                {capacityFull ? data.capacityFullLabel : data.capacityLimit}
              </div>
              {!capacityFull ? (
                <div className="capacity-limit-amount">{capacityLimit}</div>
              ) : null}
            </div>
          ) : null}

          {blockNewRegistration ? (
            <div className="form-div member-price-reminder">
              <PiWarningCircleLight aria-hidden="true" />
              <span>{data.capacityFull}</span>
            </div>
          ) : null}

          {visiblePricingRows.length > 0 && !registered && !blockNewRegistration ? (
            <>
              <div className="meeting-pricing">
                {visiblePricingRows.map((row) => {
                  const hasActiveEarlyBirdDeal = row.discount_only || (
                    Number(row.discount_amount) > 0 && Number(row.original_amount) > Number(row.amount)
                  );
                  const earlyBirdEndDate = formatPricingDate(row.discount_ends_at, language);

                  return (
                    <div className={"pricing-card" + (hasActiveEarlyBirdDeal ? " has-early-bird" : "")} key={row.tier}>
                      <div className="pricing-type">{getTierLabel(row.tier, language)}</div>
                      {hasActiveEarlyBirdDeal ? (
                        <>
                          <div className="pricing-discount">
                            {earlyBirdEndDate ? (
                              <div className="pricing-end-date">
                                {data.earlyBirdEnds}: {earlyBirdEndDate}
                              </div>
                            ) : null}
                          </div>
                          <div className="pricing-amount pricing-amount-early">
                            {formatAmount(row.amount, row.currency)}
                          </div>
                          {Number(row.original_amount) > Number(row.amount) ? (
                            <div className="pricing-original pricing-original-striked">
                              {formatAmount(row.original_amount, row.currency)}
                            </div>
                          ) : null}
                        </>
                      ) : (
                        <div className="pricing-amount">{formatAmount(row.amount, row.currency)}</div>
                      )}
                    </div>
                  );
                })}
              </div>
              {clientMembershipExpiredOrEmpty ? (
                <div className="member-price-link-row">
                  <Link className="member-price-link" to="/membership">
                    {data.memberSignupLink}
                  </Link>
                </div>
              ) : null}
            </>
          ) : null}

          {!userData?.id && !blockNewRegistration ? (
            <>
              {memberPricing ? (
                <>
                  <div className="form-div member-price-reminder">
                    <PiWarningCircleLight aria-hidden="true" />
                    <span>{data.memberPriceLoginReminder}</span>
                  </div>
                  <div className="member-price-link-row">
                    <Link className="member-price-link" to="/signup">
                      {data.memberSignupLink}
                    </Link>
                  </div>
                </>
              ) : null}
            </>
          ) : null}

          {registered ? (
            paid ? (
              <div className="success">{data.paymentCompleted}</div>
            ) : blockForCapacity ? null : hasPaidFee && savedResident === formData.resident ? (
              <EventPayment
                key={`${eventId}-${formData.resident ? "resident" : "standard"}-${membershipActive ? "member" : "nonmember"}`}
                email={formData.email}
                resident={formData.resident}
                eventId={eventId}
                eventInfo={eventInfo}
                membershipActive={membershipActive}
                language={language}
                capacityFullMessage={data.capacityFull}
              />
            ) : hasPaidFee && savedResident !== null ? (
              <div className="form-div member-price-reminder">
                <PiWarningCircleLight aria-hidden="true" />
                <span>{data.saveBeforePayment}</span>
              </div>
            ) : null
          ) : null}

          {successMsg && <div className="success">{successMsg}</div>}

          {warningMsg ? (
            <div className="form-div member-price-reminder">
              <PiWarningCircleLight aria-hidden="true" />
              <span>{warningMsg}</span>
            </div>
          ) : null}

          <form onSubmit={submit}>
            {userData?.id ? (
              <div className="form-div">
                {data.myInfoPrefix} <Link className="my-link" to="/client">{data.myInfoLink}</Link> {data.myInfoSuffix}
                {registered ? (
                  <button
                    type="button"
                    className="sync-details-button"
                    disabled={syncingDetails || loading}
                    onClick={syncDetails}
                  >
                    {syncingDetails
                      ? data.loading
                      : data.syncDetails}
                  </button>
                ) : null}
              </div>
            ) : null}
            <div className="form-div">
              <label className="sign-label">{data.lname}</label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                className="sign-input"
                disabled={userData.id || registered || blockNewRegistration}
              />
            </div>
            <div className="form-div">
              <label className="sign-label">{data.fname}</label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                className="sign-input"
                disabled={userData.id || registered || blockNewRegistration}
              />
            </div>
            <div className="form-div">
              <label className="sign-label">{data.mnumber}</label>
              <input
                type="tel"
                name="mobile_number"
                pattern="[0-9]{8}"
                value={formData.mobile_number}
                onChange={handleChange}
                required
                className="sign-input"
                disabled={userData.id || registered || blockNewRegistration}
              />
            </div>
            <div className="form-div">
              <label className="sign-label">{data.email}</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                autoCapitalize="none"
                className="sign-input"
                disabled={userData.id || registered || blockNewRegistration}
              />
            </div>
            {canChooseResident ? (
              <div className={`form-div resident-field ${paid ? "is-disabled" : ""}`}>
                <label htmlFor="event-resident">{data.resident}</label>
                <input
                  id="event-resident"
                  type="checkbox"
                  name="resident"
                  checked={formData.resident}
                  onChange={handleResidentChange}
                  disabled={paid}
                />
              </div>
            ) : null}

            {errorMsg && (
              <div className="form-div error-msg">
                <PiWarningCircleLight /> {errorMsg}
              </div>
            )}

            <button type="submit" className="sign-button" disabled={loading || paid || blockForCapacity || blockNewRegistration}>
              {submitButtonText}
            </button>
          </form>

          {userData?.id ? null : (
            <div>
              <hr />

              <button type="button" className="sign-button" disabled={loading} onClick={() => navigate("/client/signin", { state: { from: location } })}>
                {data.signin}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventRegister;
